import { useEffect, useState, useCallback } from "react";
import { Loader2, Plus, Pencil, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "../../lib/api";
import { inrCompact, PRODUCT_ICONS } from "../../lib/site";

const EMPTY = {
  name: "", slug: "", icon: "Landmark", tagline: "", short_desc: "", long_desc: "",
  min_amount: 0, max_amount: 0, rate: "", tenure: "", processing_fee: "",
  eligibility: [], documents: [], benefits: [], faqs: [], image: "",
  status: "active", order: 99, seo_title: "", seo_desc: "",
};

const toList = (s) => (Array.isArray(s) ? s : String(s).split("\n").map((x) => x.trim()).filter(Boolean));
const fromList = (a) => (Array.isArray(a) ? a.join("\n") : a || "");
const fromFaqs = (a) => (Array.isArray(a) ? a.map((f) => `${f.q} | ${f.a}`).join("\n") : "");
const toFaqs = (s) => String(s).split("\n").map((l) => l.split("|").map((x) => x.trim())).filter((p) => p[0]).map((p) => ({ q: p[0], a: p[1] || "" }));

export default function Products() {
  const [rows, setRows] = useState(null);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);

  const load = useCallback(() => api.get("/admin/products").then((r) => setRows(r.data)).catch(() => setRows([])), []);
  useEffect(() => { load(); }, [load]);

  const open = (p) => {
    setEditing(p || {});
    setForm(p ? { ...p, eligibility: fromList(p.eligibility), documents: fromList(p.documents), benefits: fromList(p.benefits), faqs: fromFaqs(p.faqs) } : { ...EMPTY, eligibility: "", documents: "", benefits: "", faqs: "" });
  };

  const save = async (e) => {
    e.preventDefault();
    const payload = {
      ...form,
      min_amount: Number(form.min_amount) || 0, max_amount: Number(form.max_amount) || 0,
      order: Number(form.order) || 99,
      eligibility: toList(form.eligibility), documents: toList(form.documents), benefits: toList(form.benefits),
      faqs: toFaqs(form.faqs),
    };
    try {
      if (editing?.id) await api.put(`/admin/products/${editing.id}`, payload);
      else await api.post("/admin/products", payload);
      toast.success("Product saved");
      setEditing(null);
      load();
    } catch (err) { toast.error(apiError(err)); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this product? Its public page will return 404.")) return;
    await api.delete(`/admin/products/${id}`);
    toast.success("Product deleted");
    load();
  };

  const inp = (label, key, props = {}) => (
    <div>
      <label className="text-[10px] uppercase tracking-wider font-bold text-brand-muted">{label}</label>
      <input className="input-site mt-1" value={form[key]} onChange={(e) => setForm({ ...form, [key]: e.target.value })} data-testid={`prod-${key}`} {...props} />
    </div>
  );
  const area = (label, key, hint) => (
    <div className="sm:col-span-3">
      <label className="text-[10px] uppercase tracking-wider font-bold text-brand-muted">{label}{hint && <span className="normal-case font-medium text-brand-muted/70"> — {hint}</span>}</label>
      <textarea className="input-site mt-1 min-h-[80px]" value={form[key]} onChange={(e) => setForm({ ...form, [key]: e.target.value })} data-testid={`prod-${key}`} />
    </div>
  );

  return (
    <div data-testid="admin-products-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-heading text-2xl font-extrabold text-brand-dark">Loan Products</h1>
          <p className="text-sm text-brand-muted mt-0.5">Active products appear on the public website instantly</p>
        </div>
        <button onClick={() => open(null)} data-testid="add-product-btn" className="btn-primary !px-5 !py-2.5 !text-xs"><Plus size={14} /> Add Product</button>
      </div>

      {editing && (
        <form onSubmit={save} className="bg-white rounded-xl border border-black/5 p-5 mb-6 grid sm:grid-cols-3 gap-4" data-testid="product-form">
          {inp("Product Name *", "name", { required: true })}
          {inp("Slug (blank = auto)", "slug")}
          <div>
            <label className="text-[10px] uppercase tracking-wider font-bold text-brand-muted">Icon</label>
            <select className="input-site mt-1" value={form.icon} onChange={(e) => setForm({ ...form, icon: e.target.value })} data-testid="prod-icon">
              {Object.keys(PRODUCT_ICONS).map((i) => <option key={i}>{i}</option>)}
            </select>
          </div>
          {inp("Tagline", "tagline")}
          {inp("Rate (e.g. 10.99% p.a. onwards*)", "rate")}
          {inp("Tenure (e.g. 12 – 60 months)", "tenure")}
          {inp("Min Amount", "min_amount", { type: "number" })}
          {inp("Max Amount", "max_amount", { type: "number" })}
          {inp("Processing Fee", "processing_fee")}
          {inp("Hero Image URL", "image")}
          {inp("Display Order", "order", { type: "number" })}
          <div>
            <label className="text-[10px] uppercase tracking-wider font-bold text-brand-muted">Status</label>
            <select className="input-site mt-1" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} data-testid="prod-status">
              <option value="active">Active</option><option value="draft">Draft</option>
            </select>
          </div>
          {area("Short Description", "short_desc")}
          {area("Long Description", "long_desc")}
          {area("Eligibility", "eligibility", "one per line")}
          {area("Documents", "documents", "one per line")}
          {area("Benefits", "benefits", "one per line")}
          {area("FAQs", "faqs", "one per line: Question | Answer")}
          {inp("SEO Title", "seo_title")}
          <div className="sm:col-span-2">{inp("SEO Description", "seo_desc")}</div>
          <div className="sm:col-span-3 flex gap-2">
            <button type="submit" data-testid="prod-save-btn" className="btn-primary !px-5 !py-2.5 !text-xs">Save Product</button>
            <button type="button" onClick={() => setEditing(null)} className="btn-outline !px-4 !py-2.5 !text-xs"><X size={13} /></button>
          </div>
        </form>
      )}

      {!rows ? <div className="py-20 flex justify-center"><Loader2 className="animate-spin text-brand-red" size={28} /></div> : (
        <div className="bg-white rounded-xl border border-black/5 overflow-x-auto">
          <table className="w-full text-sm min-w-[760px]">
            <thead>
              <tr className="text-left text-[11px] uppercase tracking-wider text-brand-muted border-b border-black/5">
                <th className="px-5 py-3 font-bold">Product</th><th className="px-5 py-3 font-bold">Amount Range</th>
                <th className="px-5 py-3 font-bold">Rate</th><th className="px-5 py-3 font-bold">Order</th>
                <th className="px-5 py-3 font-bold">Status</th><th className="px-5 py-3 font-bold">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((p) => (
                <tr key={p.id} className="border-b border-black/5 last:border-0">
                  <td className="px-5 py-3"><p className="font-semibold text-brand-dark">{p.name}</p><p className="text-xs text-brand-muted">/loans/{p.slug}</p></td>
                  <td className="px-5 py-3 text-xs">{inrCompact(p.min_amount)} – {inrCompact(p.max_amount)}</td>
                  <td className="px-5 py-3 text-xs font-semibold text-brand-red">{p.rate}</td>
                  <td className="px-5 py-3 text-xs">{p.order}</td>
                  <td className="px-5 py-3"><span className={`text-[11px] font-bold px-2.5 py-1 rounded-full border ${p.status === "active" ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-gray-100 text-gray-500 border-gray-200"}`}>{p.status}</span></td>
                  <td className="px-5 py-3">
                    <div className="flex gap-2">
                      <button onClick={() => open(p)} data-testid={`prod-edit-${p.slug}`} className="text-brand-muted hover:text-brand-dark"><Pencil size={15} /></button>
                      <button onClick={() => remove(p.id)} data-testid={`prod-delete-${p.slug}`} className="text-brand-muted hover:text-brand-red"><Trash2 size={15} /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
